import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon.js";

const cache = new Map<string, { peaks: number[]; durationMs: number }>();

/** Decodes the track once and returns ~360 peak values for the waveform. */
async function loadPeaks(url: string): Promise<{ peaks: number[]; durationMs: number }> {
  const hit = cache.get(url);
  if (hit) return hit;
  const buf = await (await fetch(url)).arrayBuffer();
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const audio = await ctx.decodeAudioData(buf);
  const data = audio.getChannelData(0);
  const bins = 360;
  const per = Math.floor(data.length / bins);
  const peaks: number[] = [];
  for (let i = 0; i < bins; i++) { let m = 0; const off = i * per; for (let j = 0; j < per; j += 8) { const v = Math.abs(data[off + j]); if (v > m) m = v; } peaks.push(m); }
  const max = Math.max(...peaks, 0.01);
  const out = { peaks: peaks.map((p) => p / max), durationMs: Math.round(audio.duration * 1000) };
  cache.set(url, out);
  ctx.close().catch(() => {});
  return out;
}

const fmt = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

/** Waveform of the chosen nasheed with draggable in/out handles: the studio loops just this section under the clip. */
export const TrackSection: React.FC<{ url: string; startMs: number; endMs: number | null; clipMs: number; onChange: (startMs: number, endMs: number | null, transient: boolean) => void }> = ({ url, startMs, endMs, clipMs, onChange }) => {
  const [wave, setWave] = useState<{ peaks: number[]; durationMs: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { setWave(null); setErr(null); loadPeaks(url).then(setWave).catch((e) => setErr(e.message)); return () => { audio.current?.pause(); }; }, [url]);
  const dur = wave?.durationMs ?? 1;
  const end = endMs ?? dur;
  useEffect(() => {
    const c = canvas.current;
    if (!c || !wave) return;
    const W = c.width = c.clientWidth * devicePixelRatio, H = c.height = 64 * devicePixelRatio;
    const g = c.getContext("2d")!;
    g.clearRect(0, 0, W, H);
    const n = wave.peaks.length;
    for (let i = 0; i < n; i++) {
      const x = (i / n) * W, ms = (i / n) * dur;
      const inside = ms >= startMs && ms <= end;
      g.fillStyle = inside ? "#F5C542" : "rgba(150,163,155,0.45)";
      const h = Math.max(2, wave.peaks[i] * H * 0.92);
      g.fillRect(x, (H - h) / 2, Math.max(1, W / n - 1), h);
    }
  }, [wave, startMs, end]);
  const latest = useRef({ s: startMs, e: endMs as number | null });
  latest.current = { s: startMs, e: endMs };
  const pxToMs = (clientX: number) => { const r = box.current!.getBoundingClientRect(); return Math.max(0, Math.min(dur, ((clientX - r.left) / r.width) * dur)); };
  const drag = (e: React.PointerEvent, which: "start" | "end" | "move") => {
    e.preventDefault(); e.stopPropagation();
    const s0 = startMs, e0 = end, x0 = e.clientX;
    const move = (ev: PointerEvent) => {
      const delta = pxToMs(ev.clientX) - pxToMs(x0);
      let ns = s0, ne: number | null = endMs == null ? null : e0;
      if (which === "start") ns = Math.max(0, Math.min(e0 - 2000, s0 + delta));
      else if (which === "end") ne = Math.min(dur, Math.max(s0 + 2000, e0 + delta));
      else { const len = e0 - s0; ns = Math.max(0, Math.min(dur - len, s0 + delta)); ne = ns + len; }
      latest.current = { s: ns, e: ne };
      onChange(ns, ne, true);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); onChange(latest.current.s, latest.current.e, false); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const preview = () => {
    if (playing) { audio.current?.pause(); setPlaying(false); return; }
    const a = new Audio(url); a.volume = 0.7; a.currentTime = startMs / 1000; a.play().catch(() => {});
    a.ontimeupdate = () => { if (a.currentTime * 1000 >= end - 50) a.currentTime = startMs / 1000; };
    a.onended = () => setPlaying(false);
    audio.current = a; setPlaying(true);
  };
  if (err) return <span className="muted" style={{ fontSize: 12 }}>Could not read this track: {err}</span>;
  const left = (startMs / dur) * 100, right = (end / dur) * 100;
  const repeats = Math.max(1, Math.ceil(clipMs / Math.max(1000, end - startMs)));
  return (
    <div className="col" style={{ gap: 6 }}>
      <div ref={box} style={{ position: "relative", height: 64, borderRadius: 8, background: "var(--panel2)", border: "1px solid var(--line)", overflow: "hidden", cursor: wave ? "grab" : "default" }} onPointerDown={(e) => { if (!wave) return; const ms = pxToMs(e.clientX); if (ms > startMs && ms < end) drag(e, "move"); }}>
        <canvas ref={canvas} style={{ width: "100%", height: 64, display: "block" }} />
        {!wave ? <span className="muted" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>Reading the track…</span> : null}
        <div onPointerDown={(e) => drag(e, "start")} style={{ position: "absolute", left: `calc(${left}% - 5px)`, top: 0, width: 10, height: 64, background: "var(--gold)", cursor: "ew-resize", borderRadius: 2 }} title="Drag: section start" />
        <div onPointerDown={(e) => drag(e, "end")} style={{ position: "absolute", left: `calc(${right}% - 5px)`, top: 0, width: 10, height: 64, background: "var(--gold)", cursor: "ew-resize", borderRadius: 2 }} title="Drag: section end" />
      </div>
      <div className="row" style={{ fontSize: 12, gap: 8 }}>
        <button className="btn sm" onClick={preview}><Icon name={playing ? "pause" : "play"} size={12} sw={0} color="currentColor" />{playing ? "Stop" : "Play section"}</button>
        <span className="muted">{fmt(startMs)} → {fmt(end)} · {((end - startMs) / 1000).toFixed(0)}s, repeats {repeats}× under the clip</span>
        {endMs != null || startMs > 0 ? <button className="btn sm" style={{ marginLeft: "auto" }} onClick={() => onChange(0, null, false)}>Whole track</button> : null}
      </div>
    </div>
  );
};
